import { useEffect, useState, useCallback } from "react";
import { Map, Gift, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

const RARITY_COLOR = {
  rare: "text-sky-300 border-sky-700/50",
  elite: "text-violet-300 border-violet-700/50",
  legendary: "text-amber-300 border-amber-600/60",
};

export function CampaignRewardTestPanel() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [pending, setPending] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/campaign/provinces")
      .then((r) => { setData(r.data.provinces || []); setPending(r.data.pending_legendary_reward || null); })
      .catch(() => setData([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const complete = async (pid, name) => {
    setBusyId(pid);
    try {
      const r = await api.post("/admin/campaign/complete-province", { province_id: pid, force: true });
      toast.success(
        r.data.capstone_queued
          ? `${name} completed — ${r.data.pending_legendary_reward?.rarity} capstone queued${r.data.training_boost ? " + training boost" : ""}! Claim on the Campaign page.`
          : `${name} completed (+${r.data.gold_reward}g)${r.data.training_boost ? " — training boost ready to assign" : ""}.`,
        { icon: <Gift size={18} className="text-amber-400" /> });
      setPending(r.data.pending_legendary_reward || null);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusyId(null); }
  };

  return (
    <div data-testid="campaign-reward-test-panel" className="mb-6 border border-amber-700/40 bg-gradient-to-br from-amber-950/20 to-zinc-950 p-4 rounded-sm">
      <div className="flex items-center gap-2 mb-3">
        <Map size={16} className="text-amber-400" />
        <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Campaign Reward Tester</h3>
      </div>
      <p className="text-sm text-zinc-500 mb-4">
        Instantly complete a province's finale <span className="text-amber-300">on your own campaign</span> to test its rewards. Provinces with a capstone re-queue the gear reward (force) so you can claim &amp; re-roll it on the Campaign page.
      </p>
      {pending && (
        <div data-testid="campaign-reward-pending" className="mb-3 text-xs text-amber-300 border border-amber-700/50 bg-amber-950/20 px-3 py-2 rounded-sm">
          Pending capstone ready to claim: <span className="font-semibold capitalize">{pending.rarity}</span> — {pending.province_name}. Open the Campaign page to claim it.
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-2">
        {(data || []).map((p) => (
          <div key={p.id} data-testid={`campaign-province-${p.id}`}
            className="flex items-center justify-between gap-3 border border-zinc-800 bg-zinc-950/60 px-3 py-2.5 rounded-sm">
            <div className="min-w-0">
              <div className="text-sm text-zinc-200 flex items-center gap-1.5">
                {p.name}
                {p.completed && <Check size={13} className="text-emerald-500" title="Completed" />}
              </div>
              {p.capstone_rarity ? (
                <span className={`inline-block mt-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${RARITY_COLOR[p.capstone_rarity] || "text-zinc-400 border-zinc-700"}`}>
                  {p.capstone_rarity} capstone
                </span>
              ) : (
                <span className="inline-block mt-1 text-[10px] uppercase tracking-wider text-zinc-600">no capstone</span>
              )}
            </div>
            <button data-testid={`campaign-complete-${p.id}`} onClick={() => complete(p.id, p.name)} disabled={busyId === p.id}
              className="shrink-0 px-3 py-1.5 font-cinzel uppercase tracking-wide text-xs rounded-sm bg-amber-500 text-zinc-950 hover:bg-amber-400 disabled:opacity-50">
              {busyId === p.id ? "Completing…" : "Complete finale"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
